import React, { useState, useEffect } from 'react';
import SignaturePad from './SignaturePad';
import { supabase } from './supabaseClient';
import { useRef } from 'react';
import { t } from './translations';
import { CardNumberElement, CardExpiryElement, CardCvcElement, useStripe, useElements } from '@stripe/react-stripe-js';

const T = { soft:'#8892a4' };

function PBar({steps,cur}){
  return(
    <div className="pgbar">
      {Array.from({length:steps}).map((_,i)=>(
        <div key={i} className={'pbs'+(i<cur?' dn':i===cur?' ac':'')}/>
      ))}
    </div>
  );
}

function PhotoSlot({idx, photos, setPhotos, load, lang}){
  const inputRef = useRef(null);
  const status = photos[idx];
  const handleFile = async (e) => {
    const file = e.target.files[0];
    if(!file) return;
    setPhotos(p=>{const n=[...p];n[idx]='uploading';return n;});
    const ext = file.name.split('.').pop();
    const path = `${load?.loadRef||'unknown'}/cargo_${idx}_${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('shipment-documents').upload(path, file);
    if(error){
      console.error('خطا در آپلود عکس:', error);
      alert('خطا در آپلود عکس: '+error.message);
      setPhotos(p=>{const n=[...p];n[idx]=null;return n;});
      return;
    }
    setPhotos(p=>{const n=[...p];n[idx]=path;return n;});
  };
  return(
    <div className={'photo-slot'+(status&&status!=='uploading'?' done':'')} onClick={()=>status!=='uploading'&&inputRef.current?.click()}>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" style={{display:'none'}} onChange={handleFile}/>
      {status==='uploading'?(<><div style={{fontSize:20}}>⏳</div><div style={{fontSize:10,color:'#fcd34d'}}>{t(lang,'uploadingLbl')}</div></>)
      :status?(<><div style={{fontSize:20}}>✓</div><div style={{fontSize:10,color:'#86efac',fontWeight:600}}>{t(lang,'photoLbl')} {idx+1}</div></>)
      :(<><div style={{fontSize:20}}>📸</div><div style={{fontSize:10,color:'#4a5568'}}>{t(lang,'photoLbl')} {idx+1}</div></>)}
    </div>
  );
}

// ── فاصله‌ی واقعی بین دو مختصات جغرافیایی (متر) — فرمول Haversine ──
const PICKUP_PROXIMITY_METERS=300; // آستانه‌ی نزدیکی مجاز به مبدا (قابل تنظیم)
function distanceMeters(lat1,lng1,lat2,lng2){
  const R=6371000;
  const dLat=(lat2-lat1)*Math.PI/180;
  const dLng=(lng2-lng1)*Math.PI/180;
  const a=Math.sin(dLat/2)*Math.sin(dLat/2)+
    Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*
    Math.sin(dLng/2)*Math.sin(dLng/2);
  const c=2*Math.atan2(Math.sqrt(a),Math.sqrt(1-a));
  return R*c;
}

export default function AcceptLoadModal({
  selLoad, acceptStep, setAcceptStep,
  insPolicyNum, setInsPolicyNum, insCompany, setInsCompany,
  insExpiry, setInsExpiry, insEuloadPaid, setInsEuloadPaid,
  photos, setPhotos,
  sigSender, setSigSender, sigSenderName, setSigSenderName,
  sigDriver, setSigDriver, driverName,
  userRole, driverPlate, carrierPlates,
  close, saveShipmentToDb, setActiveLoad, setAvail, resetAccept, lang
}){
  const load=selLoad;
  const insuranceFee=load?Math.round(load.price*0.01):0;

  // ── تأیید پلاک برای راننده‌ی مستقل (نه Carrier) — باید پلاک ثبت‌شده را تایپ کند ──
  const [plateInput,setPlateInput]=useState('');
  const [plateVerified,setPlateVerified]=useState(false);
  const [plateError,setPlateError]=useState('');
  const isIndependentDriver=userRole==='driver';
  console.log('🔍 DEBUG acceptStep:',acceptStep,'userRole:',userRole,'isIndependentDriver:',isIndependentDriver,'driverPlate:',driverPlate);

  // ── چک نزدیکی GPS راننده به مختصات مبدا (فقط برای مرحله‌ی عکس‌برداری) ──
  const [gpsStatus,setGpsStatus]=useState('idle'); // idle | checking | ok | far | unavailable | no_coords
  const [gpsDistance,setGpsDistance]=useState(null);

  const [gpsDebugCoords,setGpsDebugCoords]=useState(null);
  const checkGpsProximity=()=>{
    if(!load?.pickupLat||!load?.pickupLng){setGpsStatus('no_coords');return;}
    if(!navigator.geolocation){setGpsStatus('unavailable');return;}
    setGpsStatus('checking');
    navigator.geolocation.getCurrentPosition(
      (pos)=>{
        setGpsDebugCoords({lat:pos.coords.latitude,lng:pos.coords.longitude,accuracy:pos.coords.accuracy});
        const d=distanceMeters(pos.coords.latitude,pos.coords.longitude,load.pickupLat,load.pickupLng);
        setGpsDistance(Math.round(d));
        setGpsStatus(d<=PICKUP_PROXIMITY_METERS?'ok':'far');
      },
      ()=>{setGpsStatus('unavailable');},
      {enableHighAccuracy:true,timeout:15000,maximumAge:0}
    );
  };

  useEffect(()=>{
    if(acceptStep!==1) return;
    checkGpsProximity();
  },[acceptStep,load?.pickupLat,load?.pickupLng]);

  const gpsBlocking=gpsStatus==='far'||gpsStatus==='checking'||gpsStatus==='unavailable';

  // ── چک/پرداخت اشتراک پلاک برای شرکت‌های حمل‌ونقل (Carrier) ──
  const isCarrier=userRole==='carrier';
  const [vehicleCheckStatus,setVehicleCheckStatus]=useState('idle'); // idle | checking | already_paid | needs_payment | paying | paid | failed
  const [vehicleClientSecret,setVehicleClientSecret]=useState(null);
  const [vehiclePaymentIntentId,setVehiclePaymentIntentId]=useState(null);
  const [vehiclePaymentError,setVehiclePaymentError]=useState('');
  const [vehiclePlateInput,setVehiclePlateInput]=useState('');
  const [vehicleCardReady,setVehicleCardReady]=useState(false);

  const checkVehiclePayment=async()=>{
    if(!vehiclePlateInput||vehiclePlateInput.trim().length<3){
      setVehiclePaymentError(lang==='de'?'Bitte gültiges Kennzeichen eingeben.':'Please enter a valid plate number.');
      setVehicleCheckStatus('failed');
      return;
    }
    setVehicleCheckStatus('checking');
    setVehiclePaymentError('');
    try{
      const res=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/check-vehicle-payment',{
        method:'POST',
        headers:{
          'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Content-Type':'application/json',
        },
        body:JSON.stringify({ plate_number:vehiclePlateInput.trim() }),
      });
      const data=await res.json();
      if(data.error){
        setVehiclePaymentError(data.error);
        setVehicleCheckStatus('failed');
        return;
      }
      if(data.already_paid){
        setVehicleCheckStatus('already_paid');
      }else{
        setVehicleClientSecret(data.client_secret);
        setVehiclePaymentIntentId(data.payment_intent_id);
        setVehicleCheckStatus('needs_payment');
      }
    }catch(err){
      console.error('خطای کلی checkVehiclePayment:',err);
      setVehiclePaymentError(String(err));
      setVehicleCheckStatus('failed');
    }
  };

  // ── وضعیت پرداخت (Authorization قبل از شروع سفر) ──
  const stripe=useStripe();
  const elements=useElements();
  const [paymentStatus,setPaymentStatus]=useState('idle'); // idle | processing | authorized | failed
  const [paymentError,setPaymentError]=useState('');

  useEffect(()=>{
    if(vehicleCheckStatus!=='needs_payment') return;
    setVehicleCardReady(false);
    const interval=setInterval(()=>{
      if(elements&&elements.getElement(CardNumberElement)){
        setVehicleCardReady(true);
        clearInterval(interval);
      }
    },300);
    return()=>clearInterval(interval);
  },[vehicleCheckStatus,elements]);

  const handlePaymentAuthorize=async()=>{
    if(!stripe||!elements||!load) return;
    setPaymentStatus('processing');
    setPaymentError('');
    try{
      // قدم ۱: ساخت PaymentIntent از طریق Edge Function
      const res=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/create-payment',{
        method:'POST',
        headers:{
          'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
          'Content-Type':'application/json',
        },
        body:JSON.stringify({
          load_ref:load.loadRef,
          amount:load.price,
        }),
      });
      const data=await res.json();
      if(data.error||!data.client_secret){
        setPaymentError(data.error||'خطا در ساخت پرداخت');
        setPaymentStatus('failed');
        return;
      }

      // قدم ۲: تأیید کارت با client_secret
      const cardElement=elements.getElement(CardNumberElement);
      const {error,paymentIntent}=await stripe.confirmCardPayment(data.client_secret,{
        payment_method:{ card:cardElement },
      });

      if(error){
        setPaymentError(error.message);
        setPaymentStatus('failed');
        return;
      }

      if(paymentIntent&&paymentIntent.status==='requires_capture'){
        setPaymentStatus('authorized');
      }else{
        setPaymentError('وضعیت غیرمنتظره: '+paymentIntent?.status);
        setPaymentStatus('failed');
      }
    }catch(err){
      console.error('خطای کلی پرداخت:',err);
      setPaymentError(String(err));
      setPaymentStatus('failed');
    }
  };

  if(acceptStep===-2&&isIndependentDriver) return(
    <div>
      <div className="ttl">{lang==='de'?'Kennzeichen bestätigen':'Confirm plate number'}</div>
      <div className="sub">{lang==='de'?'Bitte geben Sie das Kennzeichen Ihres registrierten Fahrzeugs ein, um fortzufahren.':'Please enter your registered vehicle plate number to continue.'}</div>
      <div className="sec-lbl">{lang==='de'?'Kennzeichen':'Plate number'}</div>
      <input className="inp" placeholder={lang==='de'?'z.B. B-AB 1234':'e.g. B-AB 1234'} value={plateInput} onChange={e=>{setPlateInput(e.target.value);setPlateError('');}}/>
      {plateError&&<div className="warn-box">⚠️ {plateError}</div>}
      <button className="btn ba" disabled={!plateInput} onClick={()=>{
        const normalize=s=>(s||'').replace(/\s+/g,'').toUpperCase();
        if(normalize(plateInput)===normalize(driverPlate)){
          setPlateVerified(true);
          setAcceptStep(0);
        }else{
          setPlateError(lang==='de'?'Das Kennzeichen stimmt nicht mit Ihrem registrierten Fahrzeug überein.':'This plate does not match your registered vehicle.');
        }
      }}>{lang==='de'?'Bestätigen':'Confirm'}</button>
      <button className="btn bo" onClick={close}>{t(lang,'cancelBtn')}</button>
    </div>
  );
  if(acceptStep===-1) return(
    <div>
      <div className="ttl">{lang==='de'?'Fahrzeug-Freischaltung':'Vehicle Activation'}</div>
      <div className="sub">{lang==='de'?'Bitte geben Sie das Kennzeichen des Fahrzeugs ein, das diese Ladung transportiert.':'Please enter the plate number of the vehicle transporting this load.'}</div>
      {(vehicleCheckStatus==='idle'||vehicleCheckStatus==='failed')&&(
        <>
          <div className="sec-lbl">{lang==='de'?'Kennzeichen auswählen':'Select plate number'}</div>
          {carrierPlates&&carrierPlates.length>0?(
            <select className="sel" value={vehiclePlateInput} onChange={e=>setVehiclePlateInput(e.target.value)}>
              <option value="">{lang==='de'?'Bitte wählen...':'Please select...'}</option>
              {carrierPlates.map((p,idx)=>(<option key={idx} value={p}>{p}</option>))}
            </select>
          ):(
            <div style={{fontSize:12,color:T.soft,marginBottom:10}}>{lang==='de'?'Keine Fahrzeuge registriert. Bitte fügen Sie Fahrzeuge in Ihrem Profil hinzu.':'No vehicles registered. Please add vehicles in your profile.'}</div>
          )}
          {vehiclePaymentError&&<div className="warn-box">⚠️ {vehiclePaymentError}</div>}
          <button className="btn ba" disabled={!vehiclePlateInput} onClick={checkVehiclePayment}>{lang==='de'?'Prüfen':'Check'}</button>
        </>
      )}
      {vehicleCheckStatus==='checking'&&(
        <div style={{background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#fcd34d',textAlign:'center'}}>⏳ {lang==='de'?'Wird geprüft...':'Checking...'}</div>
      )}
      {vehicleCheckStatus==='already_paid'&&(
        <>
          <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#86efac',textAlign:'center'}}>✓ {lang==='de'?'Bereits für diesen Monat bezahlt':'Already paid for this month'}</div>
          <button className="btn ba" onClick={()=>setAcceptStep(0)}>{t(lang,'continueBtn')}</button>
        </>
      )}
      {(vehicleCheckStatus==='needs_payment'||vehicleCheckStatus==='paying')&&(
        <>
          <div style={{background:'rgba(232,240,26,0.05)',border:'1px solid rgba(232,240,26,0.15)',borderRadius:10,padding:11,marginBottom:14,fontSize:12,color:T.soft}}>
            {lang==='de'?'Monatliche Gebühr':'Monthly fee'}: <strong style={{color:'#e8f01a'}}>39 EUR</strong>
          </div>
          <div style={{background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:'14px',marginBottom:10}}>
            <div style={{fontSize:11,color:T.soft,marginBottom:5}}>{lang==='de'?'Kartennummer':'Card number'}</div>
            <CardNumberElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
          </div>
          <div style={{display:'flex',gap:10,marginBottom:14}}>
            <div style={{flex:1,background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:'14px'}}>
              <div style={{fontSize:11,color:T.soft,marginBottom:5}}>{lang==='de'?'Ablaufdatum':'Expiry'}</div>
              <CardExpiryElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
            </div>
            <div style={{flex:1,background:'#181d25',border:'1px solid #232b36',borderRadius:11,padding:'14px'}}>
              <div style={{fontSize:11,color:T.soft,marginBottom:5}}>CVC</div>
              <CardCvcElement options={{style:{base:{fontSize:'15px',color:'#e2e8f0','::placeholder':{color:'#4a5568'}},invalid:{color:'#fc8181'}}}}/>
            </div>
          </div>
          {vehiclePaymentError&&<div className="warn-box">⚠️ {vehiclePaymentError}</div>}
          <button className="btn ba" disabled={vehicleCheckStatus==='paying'||!stripe||!vehicleCardReady} onClick={async()=>{
            console.log('🟡 کلیک شد. stripe:',!!stripe,'elements:',!!elements,'vehicleClientSecret:',vehicleClientSecret);
            if(!stripe||!elements) return;
            setVehicleCheckStatus('paying');
            setVehiclePaymentError('');
            try{
              const cardElement=elements.getElement(CardNumberElement);
              if(!cardElement){
                setVehiclePaymentError(lang==='de'?'Karte noch nicht bereit, bitte kurz warten und erneut versuchen.':'Card not ready yet, please wait a moment and try again.');
                setVehicleCheckStatus('needs_payment');
                return;
              }
              const {error,paymentIntent}=await stripe.confirmCardPayment(vehicleClientSecret,{
                payment_method:{ card:cardElement },
              });
              if(error){
                setVehiclePaymentError(error.message);
                setVehicleCheckStatus('needs_payment');
                return;
              }
              if(paymentIntent&&paymentIntent.status==='succeeded'){
                console.log('🟣 در حال فراخوانی confirm-vehicle-payment برای پلاک:',vehiclePlateInput.trim());
                const confirmRes=await fetch('https://oqnrxrowiewwrtroegwl.supabase.co/functions/v1/confirm-vehicle-payment',{
                  method:'POST',
                  headers:{
                    'apikey':'sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
                    'Authorization':'Bearer sb_publishable_WwQgxEBYbgBgCCpuC7hqTA_4pv48QMK',
                    'Content-Type':'application/json',
                  },
                  body:JSON.stringify({ plate_number:vehiclePlateInput.trim(), payment_intent_id:vehiclePaymentIntentId }),
                });
                const confirmData=await confirmRes.json();
                console.log('🟣 پاسخ confirm-vehicle-payment:',confirmData);
                setVehicleCheckStatus('paid');
              }else{
                setVehiclePaymentError('وضعیت غیرمنتظره: '+paymentIntent?.status);
                setVehicleCheckStatus('needs_payment');
              }
            }catch(err){
              console.error('خطای کلی پرداخت پلاک:',err);
              setVehiclePaymentError(String(err));
              setVehicleCheckStatus('needs_payment');
            }
          }}>
            {vehicleCheckStatus==='paying'?(lang==='de'?'Wird verarbeitet...':'Processing...'):(lang==='de'?'Jetzt bezahlen':'Pay now')}
          </button>
        </>
      )}
      {vehicleCheckStatus==='paid'&&(
        <>
          <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#86efac',textAlign:'center'}}>✓ {lang==='de'?'Zahlung erfolgreich':'Payment successful'}</div>
          <button className="btn ba" onClick={()=>setAcceptStep(0)}>{t(lang,'continueBtn')}</button>
        </>
      )}
      
      <button className="btn bo" onClick={close}>{t(lang,'cancelBtn')}</button>
    </div>
  );
  if(acceptStep===0) return(
    <div>
      <PBar steps={6} cur={0}/>
      <div style={{fontFamily:'Barlow Condensed',fontSize:11,color:'#4a5568',letterSpacing:1,marginBottom:8}}>REF: {load?.loadRef||'—'}</div>
      <div className="ttl">{t(lang,'insuranceCheckTitle')}</div>
      <div className="sub">{t(lang,'insuranceCheckSub')}</div>
      {load?.insured?(
        <div>
          <div className="ins-box">
            <div style={{fontSize:12,color:'#86efac',fontWeight:600,marginBottom:10}}>🛡 {t(lang,'shipperDeclaredInsured')}</div>
            <div style={{fontSize:11,color:T.soft,marginBottom:13}}>{t(lang,'enterPolicyDetailsNote')}</div>
            <div className="sec-lbl">{t(lang,'policyNumberLbl')}</div>
            <input className="inp" placeholder={t(lang,'policyNumberPh')} value={insPolicyNum} onChange={e=>setInsPolicyNum(e.target.value)}/>
            <div className="sec-lbl">{t(lang,'insuranceCompanyLbl')}</div>
            <input className="inp" placeholder={t(lang,'insuranceCompanyPh')} value={insCompany} onChange={e=>setInsCompany(e.target.value)}/>
            <div className="sec-lbl">{t(lang,'policyExpiryLbl')}</div>
            <input className="inp" type="date" value={insExpiry} onChange={e=>setInsExpiry(e.target.value)}/>
          </div>
          <button className="btn ba" disabled={!insPolicyNum||insPolicyNum.length<5||!insCompany||insCompany.length<3||!insExpiry||new Date(insExpiry)<new Date()} onClick={()=>setAcceptStep(1)}>{t(lang,'confirmInsuranceContinue')}</button>
          {insExpiry&&new Date(insExpiry)<new Date()&&(<div className="warn-box">{t(lang,'policyExpiredWarning')}</div>)}
        </div>
      ):(
        <div>
          <div className="ins-box-red">
            <div style={{fontSize:12,color:'#fc8181',fontWeight:600,marginBottom:8}}>⚡ {t(lang,'noInsuranceTitle')}</div>
            <div style={{fontSize:11,color:T.soft,marginBottom:13}}>{t(lang,'noInsuranceSub')}</div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderTop:'1px solid #232b36',borderBottom:'1px solid #232b36',margin:'10px 0'}}>
              <div>
                <div style={{fontSize:13,fontWeight:600}}>{t(lang,'euloadInsuranceLbl')}</div>
                <div style={{fontSize:11,color:T.soft}}>{t(lang,'euloadInsuranceDesc')}</div>
              </div>
              <div style={{fontFamily:'Barlow Condensed',fontSize:20,fontWeight:900,color:'#e8f01a'}}>{insuranceFee} EUR</div>
            </div>
            <div style={{fontSize:11,color:T.soft,marginTop:8,lineHeight:1.7,whiteSpace:'pre-line'}}>{t(lang,'euloadInsuranceFeatures')}</div>
          </div>
          {!insEuloadPaid?(
            <button className="btn ba" onClick={()=>setInsEuloadPaid(true)}>{t(lang,'payEuloadInsuranceBtn')} {insuranceFee} {t(lang,'euloadInsuranceEurSuffix')}</button>
          ):(
            <div>
              <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:10,padding:11,marginBottom:13,fontSize:12,color:'#86efac',textAlign:'center'}}>✓ {t(lang,'euloadInsurancePaidConfirm')}</div>
              <button className="btn ba" onClick={()=>setAcceptStep(1)}>{t(lang,'continueBtn')}</button>
            </div>
          )}
        </div>
      )}
      <button className="btn bo" onClick={close}>{t(lang,'cancelBtn')}</button>
    </div>
  );
  if(acceptStep===1) return(
    <div>
      <PBar steps={6} cur={1}/>
      <div className="ttl">{t(lang,'cargoPhotosTitle')}</div>
      <div className="sub">{t(lang,'cargoPhotosSub')}</div>
      {gpsStatus==='checking'&&(
        <div style={{background:'rgba(245,158,11,0.07)',border:'1px solid rgba(245,158,11,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#fcd34d',textAlign:'center'}}>⏳ {lang==='de'?'Standort wird geprüft...':'Checking your location...'}</div>
      )}
      {gpsStatus==='far'&&(
        <div style={{background:'rgba(239,68,68,0.07)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#fc8181',lineHeight:1.7}}>
          ⚠️ {lang==='de'?`Sie sind ${gpsDistance}m vom Abholort entfernt. Sie müssen sich am Abholort befinden, um Fotos zu machen.`:`You are ${gpsDistance}m away from the pickup location. You must be at the pickup location to take photos.`}
          <div style={{fontSize:10,color:'#fcd34d',marginTop:8,fontFamily:'monospace'}}>DEBUG pickup: {load?.pickupLat}, {load?.pickupLng}</div>
          <div style={{fontSize:10,color:'#fcd34d',fontFamily:'monospace'}}>DEBUG you: {gpsDebugCoords?.lat}, {gpsDebugCoords?.lng}</div>
          <button onClick={checkGpsProximity} style={{width:'100%',marginTop:10,padding:'8px',background:'rgba(239,68,68,0.1)',color:'#fc8181',border:'1px solid rgba(239,68,68,0.4)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>🔄 {lang==='de'?'Standort erneut prüfen':'Recheck location'}</button>
        </div>
      )}
      {gpsStatus==='unavailable'&&(
        <div style={{background:'rgba(239,68,68,0.07)',border:'1px solid rgba(239,68,68,0.25)',borderRadius:10,padding:13,marginBottom:14,fontSize:12,color:'#fc8181',lineHeight:1.7}}>
          ⚠️ {lang==='de'?'Standort konnte nicht ermittelt werden. Bitte GPS aktivieren und erneut versuchen.':'Could not determine your location. Please enable GPS and try again.'}
          <button onClick={checkGpsProximity} style={{width:'100%',marginTop:10,padding:'8px',background:'rgba(239,68,68,0.1)',color:'#fc8181',border:'1px solid rgba(239,68,68,0.4)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>🔄 {lang==='de'?'Standort erneut prüfen':'Recheck location'}</button>
        </div>
      )}
      {gpsStatus==='ok'&&(
        <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.25)',borderRadius:10,padding:11,marginBottom:14,fontSize:12,color:'#86efac',textAlign:'center'}}>✓ {lang==='de'?'Standort bestätigt':'Location confirmed'}</div>
      )}
      {!gpsBlocking?(
        <>
          <div className="photo-grid">
            {photos.map((_,idx)=>(
              <PhotoSlot key={idx} idx={idx} photos={photos} setPhotos={setPhotos} load={load} lang={lang}/>
            ))}
          </div>
          <div style={{fontSize:11,color:T.soft,marginBottom:14,lineHeight:1.7,whiteSpace:'pre-line'}}>{t(lang,'photoHintText')}</div>
        </>
      ):null}
      <button className="btn ba" disabled={gpsBlocking||photos.filter(p=>p&&p!=='uploading').length<2} onClick={()=>setAcceptStep(2)}>{gpsBlocking?(lang==='de'?'Am Abholort erforderlich':'Must be at pickup location'):(photos.filter(p=>p&&p!=='uploading').length<2?`${t(lang,'photosNeededMorePrefix')} ${2-photos.filter(p=>p&&p!=='uploading').length} ${t(lang,'photosNeededMoreSuffix')}`:t(lang,'continueToSenderSig'))}</button>
      <button className="btn bo" onClick={()=>setAcceptStep(0)}>{t(lang,'backBtn')}</button>
    </div>
  );
  if(acceptStep===2) return(
    <div>
      <PBar steps={6} cur={2}/>
      <div className="ttl">{t(lang,'senderSigTitle')}</div>
      <div className="sub">{t(lang,'senderSigSub')}</div>
      <div className="sec-lbl">{t(lang,'senderNameLbl')}</div>
      <input className="inp" placeholder={t(lang,'senderNamePh')} value={sigSenderName} onChange={e=>setSigSenderName(e.target.value)}/>
      <SignaturePad label={t(lang,'senderSigLabel')} subLabel={`"${t(lang,'senderSigConfirmPrefix')} ${sigSenderName||'...'} ${t(lang,'senderSigConfirmSuffix')} ${load?.loadRef||''}"`} onSign={setSigSender} signed={!!sigSender} signerName={sigSenderName} lang={lang}/>
      <button className="btn ba" disabled={!sigSender||!sigSenderName} onClick={()=>setAcceptStep(3)}>{t(lang,'continueToDriverSig')}</button>
      <button className="btn bo" onClick={()=>setAcceptStep(1)}>{t(lang,'backBtn')}</button>
    </div>
  );
  if(acceptStep===3) return(
    <div>
      <PBar steps={6} cur={3}/>
      <div className="ttl">{t(lang,'driverSigTitle')}</div>
      <div className="sub">{t(lang,'driverSigSub')}</div>
      <div style={{background:'rgba(232,240,26,0.05)',border:'1px solid rgba(232,240,26,0.15)',borderRadius:10,padding:11,marginBottom:14,fontSize:11,color:T.soft,lineHeight:1.7}}>{t(lang,'responsibilityTransferNote')}<br/>{t(lang,'loadNumberLbl')}: <strong style={{color:'#e8f01a'}}>{load?.loadRef}</strong></div>
      <SignaturePad label={t(lang,'driverSigLabel')} subLabel={t(lang,'driverSigConfirmText')} onSign={setSigDriver} signed={!!sigDriver} signerName={driverName||t(lang,'driverWord')} lang={lang}/>
      <button className="btn ba" disabled={!sigDriver} onClick={()=>setAcceptStep(5)}>{t(lang,'finalConfirmBtn')}</button>
      <button className="btn bo" onClick={()=>setAcceptStep(2)}>{t(lang,'backBtn')}</button>
    </div>
  );
  if(acceptStep===5) return(    <div>
      <PBar steps={6} cur={5}/>
      <div className="ctr">
        <div className="success-ico" style={{background:'rgba(232,240,26,0.1)',border:'1px solid rgba(232,240,26,0.3)'}}>🚛</div>
        <div className="ttl">{t(lang,'readyToMoveTitle')}</div>
        <div className="sub">{t(lang,'readyToMoveSub')}</div>
      </div>
      <div className="pb">
        {[[t(lang,'loadNumberLbl'),load?.loadRef||'—'],[t(lang,'routeLbl'),(load?.from||'')+'  →  '+(load?.to||'')],[t(lang,'insuranceSummaryLbl'),load?.insured?`✓ ${insCompany} — ${insPolicyNum}`:'✓ EU.LOAD Insurance'],[t(lang,'photosSummaryLbl'),photos.filter(Boolean).length+' '+t(lang,'photosRegisteredSuffix')],[t(lang,'senderSigSummaryLbl'),'✓ '+sigSenderName],[t(lang,'driverSigSummaryLbl'),'✓ '+t(lang,'confirmedWord')],[lang==='de'?'Zahlung':'Payment','✓ '+(lang==='de'?'Autorisiert':'Authorized')]].map(([k,v])=>(<div key={k} className="prow" style={{marginBottom:7}}><span style={{fontSize:11,color:T.soft}}>{k}</span><span style={{fontSize:11,fontWeight:600,textAlign:'right',maxWidth:'60%',color:'#f1f5f9'}}>{v}</span></div>))}
      </div>
      <button className="btn ba" onClick={async()=>{await saveShipmentToDb(load);setActiveLoad(load);setAvail('busy');resetAccept();close();}}>{t(lang,'startTripBtn')} ✓</button>
    </div>
  );
  return null;
}